import { ApiError, ESCALATION_CLOSED_ERROR_CODE, apiErrorFromResponse, apiFetch } from './http';
import { buildPortalApiPath } from './routes';

export interface AttachmentUploadSession {
  attachmentId: string;
  uploadUrl?: string;
  headers?: Record<string, string>;
  expiresAt?: string;
  mock?: boolean;
}

export function uploadFileName(file: File): string {
  return file.name.normalize('NFKC').replace(/[^\x20-\x7E]/g, '_');
}

export async function sha256Base64(file: File): Promise<string> {
  const digest = new Uint8Array(await crypto.subtle.digest('SHA-256', await file.arrayBuffer()));
  let binary = '';
  digest.forEach((byte) => { binary += String.fromCharCode(byte); });
  return btoa(binary);
}

// Sends the bytes straight to BPS storage with the reservation's signed URL and headers.
export async function putFileToSignedUrl(
  uploadUrl: string,
  headers: Record<string, string> | undefined,
  file: File,
  signal?: AbortSignal,
): Promise<void> {
  const uploadResponse = await fetch(uploadUrl, {
    method: 'PUT',
    credentials: 'omit',
    headers,
    body: file,
    signal,
  });

  if (!uploadResponse.ok) {
    throw new ApiError('Attachment upload failed', uploadResponse.status);
  }
}

export async function createAttachmentUploadSession(
  siteType: string,
  resultId: string,
  fieldId: string,
  file: File,
  sha256: string,
  idempotencyKey: string,
): Promise<AttachmentUploadSession> {
  const response = await apiFetch(buildPortalApiPath(`/${siteType}/results/${encodeURIComponent(resultId)}/attachments/session`), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      fieldId,
      fileName: uploadFileName(file),
      fileSizeBytes: file.size,
      contentType: file.type || 'application/octet-stream',
      sha256,
      idempotencyKey,
    }),
  });

  if (!response.ok) {
    const error = await apiErrorFromResponse(response, 'Unable to prepare attachment upload');
    if (error.errorCode === ESCALATION_CLOSED_ERROR_CODE) {
      throw error;
    }
    if (response.status === 409) {
      throw new ApiError('The attachment could not be accepted.', response.status);
    }
    throw error;
  }

  return response.json() as Promise<AttachmentUploadSession>;
}

export async function finalizeAttachment(siteType: string, resultId: string, attachmentId: string): Promise<void> {
  const response = await apiFetch(buildPortalApiPath(`/${siteType}/results/${encodeURIComponent(resultId)}/attachments/${encodeURIComponent(attachmentId)}/finalize`), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: '{}',
  });

  if (!response.ok) {
    throw await apiErrorFromResponse(response, 'Unable to finalize attachment upload');
  }
}

export async function uploadAttachment(siteType: string, resultId: string, fieldId: string, file: File, idempotencyKey: string): Promise<void> {
  const sha256 = await sha256Base64(file);
  const session = await createAttachmentUploadSession(siteType, resultId, fieldId, file, sha256, idempotencyKey);
  if (session.mock) return;
  if (!session.uploadUrl) {
    throw new ApiError('Attachment upload session is missing an upload URL', 502);
  }
  await putFileToSignedUrl(session.uploadUrl, session.headers, file);

  await finalizeAttachment(siteType, resultId, session.attachmentId);
}
